import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  ListItem,
  ListItemButton,
  Menu,
  MenuItem,
  Tooltip,
  Typography,
  useTheme,
} from "@mui/material";
import { Box, Stack, useMediaQuery } from "@mui/system";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import MenuIcon from "@mui/icons-material/Menu";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import { Add as AddIcon } from "@mui/icons-material";
import React, { useContext, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { CMSContext } from "../context/CaseManagmentContext";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import CloseIcon from "@mui/icons-material/Close";
import DoneIcon from "@mui/icons-material/Done";
import { showInsufficientCreditsModal } from "../../../actions/creditActions";
import { deductCredits } from "../../../utils/HelperFunction/deductCredits";
import ConfirmationDialog from "../../../dialogs/ConfirmationDialog";
import { toast } from "react-toastify";
import AddNewCaseModal from "../../../dialogs/AddNewCaseDialog";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPenToSquare } from "@fortawesome/free-solid-svg-icons";
import { updateCaseApi, deleteCaseApi } from "../../../services/api/caseManagementApi";
import LogoLoader from "../../UI/LogoLoader";

export default function Sidebar({
  onClose,
  isCollapsed = false,
  toggleSidebar,
}) {
  const {
    selectedCase,
    setSelectedCase,
    currentTab,
    setCurrentTab,
    cases,
    setCases,
    fetchCaseLoading,
    setFetchCaseLoading,
    setIsHistoryClicked,
  } = useContext(CMSContext);
  const isMobileView = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const [searchKeyword, setSearchKeyword] = useState("");
  const [isEditing, setIsEditing] = useState({ _id: "" });
  const [expanded, setExpanded] = useState(null);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [newTitle, setNewTitle] = useState("");
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [addNewCaseModal, setAddNewCaseModal] = useState(false);
  const safeCases = Array.isArray(cases) ? cases : [];
  const handleCaseClick = (caseItem) => {
    if (isMobile) {
      onClose();
    }
    setIsHistoryClicked(false);
    setCurrentTab(1);
    setSelectedCase(caseItem);
  };
  const inputRef = useRef(null);
  const credits = useSelector((state) => state.credit.credits); // Get credits from Redux store
  const userId = useSelector((state) => state.credit.userId);
  const dispatch = useDispatch();

  useEffect(() => {
    if (safeCases.length > 0 && !selectedCase) {
      if (currentTab === 1) {
        // Sort the cases array by createdAt in descending order
        const sortedCases = [...safeCases].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );

        // Set the most recent case
        setSelectedCase(sortedCases[0]);

        // Find the category (status) of the most recent case
        const latestCase = sortedCases[0];
        setExpanded(latestCase.status); // Expand the accordion based on the latest case's category
      } else {
        setExpanded(null);
      }
    }
  }, [safeCases, currentTab, selectedCase, setSelectedCase]);

  useEffect(() => {
    if (safeCases.length > 0) {
      if (selectedCase) {
        // If a case is selected, expand the accordion for its category
        setExpanded(selectedCase.status);
      } else {
        // Find the category that contains the most recent case
        const sortedCases = [...safeCases].sort(
          (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
        );
        const latestCase = sortedCases[0];

        // Set the expanded accordion to the category of the latest case

        // Select the latest case
        if (currentTab === 1) {
          setExpanded(latestCase.status);
          setSelectedCase(latestCase);
        }
      }
    }
  }, [safeCases, selectedCase, currentTab, setSelectedCase]); // This runs when cases or selectedCase are updated

  const sortedCases = [...safeCases].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt),
  );

  const filteredData = sortedCases.filter((caseItem) =>
    caseItem.caseTitle.toLowerCase().includes(searchKeyword.toLowerCase()),
  );

  const categories = [
    { title: "In Progress", color: "orange" },
    { title: "Not Started", color: "red" },
    { title: "Completed", color: "green" },
  ];

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuOpen = (event) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };
  const open = Boolean(anchorEl);

  const handleEditTitle = (event, chatId) => {
    event.stopPropagation();
    setIsEditing({ _id: chatId });
    const findChat = cases?.find((title) => title._id === chatId);
    setNewTitle(findChat.caseTitle);
  };

  const handleInputChange = (e) => {
    const cursorPosition = inputRef.current.selectionStart;
    setNewTitle(e.target.value);

    // Set the cursor back to where it was
    setTimeout(() => {
      inputRef.current.setSelectionRange(cursorPosition, cursorPosition);
    }, 0);
  };
  const handleSaveEdit = async (caseId) => {
    if (!newTitle) return;

    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
      return;
    }

    // Call editCase API here
    try {
      setFetchCaseLoading(true);
      const response = await updateCaseApi(caseId, { ...selectedCase, caseTitle: newTitle });

      if (response.status !== 200) {
        throw new Error("Failed to update case title");
      }
      // Update the state with the new case title
      setCases((prevCases) =>
        prevCases.map((caseItem) =>
          caseItem._id === caseId
            ? { ...caseItem, caseTitle: newTitle }
            : caseItem,
        ),
      );
      toast.success("Case title updated successfully");

      // Reset editing state after saving
      setIsEditing({ _id: "" });
      setNewTitle("");

      // Deduct credits
      const deductionSuccess = deductCredits(
        {
          userId,
          creditsToDeduct: 1,
          event: "Case title edit on case management page.",
        },
        dispatch,
      );

      if (!deductionSuccess) {
        console.warn(
          "Failed to deduct credits. Ensure retry or user notification.",
        );
      }
    } catch (error) {
      console.error("Error updating case title:", error);
      toast.error("Failed to update case title");
    } finally {
      setFetchCaseLoading(false);
    }
  };

  const handleCancelEdit = (index) => {
    setIsEditing({ _id: "" });
    setNewTitle("");
  };

  const handleDeleteChat = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setAnchorEl(null);
    // Open delete dialog
    setOpenDeleteDialog(true);
  };

  const handleConfirmDelete = async (event, caseId) => {
    let creditsToDeduct = 1;
    if (credits <= 0 || credits <= creditsToDeduct) {
      dispatch(showInsufficientCreditsModal()); // Trigger modal if credits are insufficient
      return;
    }
    event.preventDefault();
    event.stopPropagation();

    // Call removeCase API here
    try {
      setFetchCaseLoading(true);
      const response = await deleteCaseApi(caseId);

      if (response.status !== 200) {
        throw new Error("Failed to delete case");
      }

      // Remove the case from the state
      setCases((prevCases) =>
        prevCases.filter((caseItem) => caseItem._id !== caseId),
      );
      setSelectedCase(null);
      toast.success("Case deleted successfully");

      // Deduct credits
      const deductionSuccess = deductCredits(
        {
          userId,
          creditsToDeduct: 1,
          event: "Delete case on Case Management page.",
        },
        dispatch,
      );

      if (!deductionSuccess) {
        console.warn(
          "Failed to deduct credits. Ensure retry or user notification.",
        );
      }
    } catch (error) {
      console.error("Error deleting case:", error);
      toast.error("Failed to delete case");
    } finally {
      setFetchCaseLoading(false);
      setOpenDeleteDialog(false);
    }
  };

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        boxSizing: "border-box",
        flex: 1,
        gap: "4px",
        height: "calc(100vh - 150px)",
        p: "12px 12px 0 12px",
        bgcolor: "#FFFFFF",
        padding: "10px",
        borderRadius: "10px",
        overflow: "hidden", // Prevents content overflow during transition
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)", // Smooth transition for all animatable properties
        ...(isMobileView
          ? {
              width: "200px",
              animation: isCollapsed ? "slideOut 0.3s" : "slideIn 0.3s", // Optional mobile animations
            }
          : {
              flexBasis: isCollapsed ? "60px" : "100%",
              flexShrink: 0,
              maxWidth: isCollapsed ? "60px" : "100%",
              // Animate these specific properties
              transitionProperty: "flex-basis, max-width",
            }),
      }}
    >
      {!isMobileView && (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignContent: "center",
            alignItems: "center",
            mb: 1,
          }}
        >
          <IconButton
            onClick={toggleSidebar}
            sx={{
              width: "40px",
              height: "40px",
            }}
          >
            {isCollapsed ? (
              <MenuIcon />
            ) : (
              <ChevronLeftIcon
                color="#0000008a"
                sx={{ color: "#0000008a", fontSize: "25px" }}
              />
            )}
          </IconButton>
          <Box
            sx={{
              display: "flex",
              alignContent: "center",
              alignItems: "center",
            }}
          >
            {!isCollapsed && (
              <Tooltip title="New Case" placement="bottom">
                <Button
                  sx={{
                    minWidth: "40px",
                    width: "40px",
                    padding: 0,
                  }}
                  onClick={() => {
                    setAddNewCaseModal(true);
                  }}
                >
                  <FontAwesomeIcon
                    icon={faPenToSquare}
                    fontSize={"18px"}
                    color={"#0000008a"}
                  />
                </Button>
              </Tooltip>
            )}
            {!isCollapsed && (
              <Tooltip title="View History" placement="bottom">
                <Box
                  sx={{
                    textAlign: "center",
                    mt: "5px",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    setIsHistoryClicked(true);
                    setSelectedCase({});
                    setExpanded(null);
                    setCurrentTab(1);
                    if (isMobile) {
                      onClose();
                    }
                  }}
                >
                  <ManageSearchIcon
                    color="#0000008a"
                    sx={{ color: "#0000008a", fontSize: "25px" }}
                  />
                </Box>
              </Tooltip>
            )}
          </Box>
        </Box>
      )}
      {/* Add Case Button */}
      {isMobileView && (
        <>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => {
              setAddNewCaseModal(true);
            }}
            sx={{
              backgroundColor: "#003D78",
              display: "flex",
              justifyContent: "center",
              marginBottom: "8px",
              textTransform: "none",
              transition: "background-color 0.3s ease-in-out",
              "&:hover": { backgroundColor: "primary.main", color: "#fff" },
            }}
          >
            Add Case
          </Button>
          <Box
            sx={{ textAlign: "right", paddingY: "8px" }}
            onClick={() => {
              setIsHistoryClicked(true);
              setSelectedCase({});
              setExpanded(null);
              setCurrentTab(1);
              if (isMobile) {
                onClose();
              }
            }}
          >
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: "bold",
                mr: 0.5,
                color: "#3C50B1",
                "&:hover": {
                  cursor: "pointer",
                  textDecoration: "underline",
                },
              }}
            >
              View History
            </Typography>
          </Box>
        </>
      )}
      {/* Search Field */}
      {/*Hidden temporarily*/}
      {/* <TextField
        placeholder={"Search Cases"}
        variant="outlined"
        value={searchKeyword}
        onChange={(e) => setSearchKeyword(e.target.value)}
        InputProps={{
          sx: {
            p: 0,
            backgroundColor: "#ffffff",
            borderRadius: "20px",
            paddingRight: "5px",
          },
        }}
        sx={{
          width: "100%",
          marginRight: "16px",
          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "#ccc",
            },
            "&:hover fieldset": {
              borderColor: "#00509e",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#00509e",
            },
          },
          "& input": {
            padding: "10px 14px",
            fontSize: "0.875rem",
          },
        }}
      /> */}
      {isCollapsed && (
        <Tooltip title="New Case" placement="right">
          <Button
            // variant="contained"
            sx={{
              // backgroundColor: "#003D78",
              minWidth: "40px",
              width: "40px",
              height: "40px",
              padding: 0,
              marginBottom: "8px",
              marginTop: "8px",
              borderRadius: "8px",
            }}
            onClick={() => {
              setAddNewCaseModal(true);
            }}
          >
            <FontAwesomeIcon
              icon={faPenToSquare}
              fontSize={"17px"}
              color="#0000008a"
            />
          </Button>
        </Tooltip>
      )}

      {isCollapsed && (
        <Tooltip title="View History" placement="right">
          <Box
            sx={{
              textAlign: "center",
              paddingY: "8px",
              cursor: "pointer",
            }}
            onClick={() => {
              setIsHistoryClicked(true);
              setSelectedCase({});
              setExpanded(null);
              setCurrentTab(1);
              if (isMobile) {
                onClose();
              }
            }}
          >
            <ManageSearchIcon color="#0000008a" sx={{ color: "#0000008a" }} />
          </Box>
        </Tooltip>
      )}
      {/* Case List */}
      {fetchCaseLoading ? (
        <Box
          sx={{
            margin: "auto",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <LogoLoader size={isMobileView ? "sm" : "md"} duration="1s" />
        </Box>
      ) : categories && categories.length > 0 && !isCollapsed ? (
        categories.map((category) => (
          <Accordion
            key={category.title}
            expanded={expanded === category.title}
            onChange={() =>
              setExpanded(expanded === category.title ? null : category.title)
            }
            sx={{
              margin: "4px 0",
              boxShadow: "0px 1px 3px rgba(0, 0, 0, 0.1)",
              borderRadius: "8px",
              "&:before": { display: "none" }, // Remove default divider line
              "&.Mui-expanded": {
                margin: "4px 0px",
              },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon fontSize="small" />}
              aria-controls={`${category.title}-content`}
              id={`${category.title}-header`}
              sx={{
                backgroundColor: "#f0f2f5",
                borderRadius: "6px",
                minHeight: 40,
                "&.Mui-expanded": {
                  minHeight: 40,
                },
                "& .MuiAccordionSummary-content": {
                  margin: 0,
                },
              }}
            >
              <Typography
                sx={{
                  fontWeight: "bold",
                  fontSize: "13px",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                {category.title} (
                {
                  filteredData.filter(
                    (caseItem) => caseItem.status === category.title,
                  ).length
                }
                )
              </Typography>
            </AccordionSummary>

            <AccordionDetails
              sx={{
                padding: "4px 0px",
                backgroundColor: "#f9f9fb",
                maxHeight: "200px",
                overflowY: "auto",
                "::-webkit-scrollbar": {
                  width: "5px",
                },
                "::-webkit-scrollbar-track": {
                  background: "#fafafa",
                },
                "::-webkit-scrollbar-thumb": {
                  background: "#bbb",
                  borderRadius: "6px",
                },
              }}
            >
              {filteredData?.filter(
                (caseItem) => caseItem.status === category.title,
              ).length === 0 ? (
                <Typography
                  variant="subtitle2"
                  sx={{
                    p: 0.5,
                    textAlign: "center",
                    fontSize: "13px",
                  }}
                >
                  No cases available
                </Typography>
              ) : (
                filteredData
                  ?.filter((caseItem) => caseItem.status === category.title)
                  ?.map((caseItem) => (
                    <Box key={caseItem._id}>
                      <ListItem disablePadding>
                        <ListItemButton
                          disableRipple
                          sx={{
                            width: "100%",
                            mx: 1,
                            marginY: "4px",
                            borderRadius: "6px",
                            height: "38px",
                            padding: "8px 12px",
                            "&:hover": {
                              backgroundColor: "#e8eefc",
                            },
                            backgroundColor:
                              selectedCase?._id === caseItem._id
                                ? "#e8eefc"
                                : "inherit",
                          }}
                          onClick={() => handleCaseClick(caseItem)}
                        >
                          {isEditing?._id === caseItem?._id ? (
                            <Stack direction="row">
                              <input
                                ref={inputRef}
                                style={{
                                  width: "100%",
                                  marginLeft: "0",
                                  fontSize: "13px",
                                }}
                                type="text"
                                autoFocus
                                value={newTitle}
                                onClick={(e) => e.stopPropagation()}
                                onFocus={(e) => e.stopPropagation()}
                                onChange={handleInputChange}
                              />
                              <IconButton
                                edge="end"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleSaveEdit(caseItem._id);
                                  setAnchorEl(null);
                                }}
                              >
                                <DoneIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                edge="end"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleCancelEdit();
                                  setAnchorEl(null);
                                }}
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            </Stack>
                          ) : (
                            <Stack
                              direction="row"
                              width="100%"
                              justifyContent="space-between"
                              alignItems="center"
                            >
                              <Typography
                                variant="subtitle2"
                                noWrap
                                sx={{
                                  textOverflow: "ellipsis",
                                  overflow: "hidden",
                                  whiteSpace: "nowrap",
                                  fontSize: "13px",
                                }}
                              >
                                {caseItem.caseTitle}
                              </Typography>
                              {selectedCase?._id === caseItem?._id && (
                                <>
                                  <IconButton
                                    size="small"
                                    sx={{
                                      p: 0,
                                    }}
                                    id="basic-button"
                                    aria-controls={
                                      open ? "basic-menu" : undefined
                                    }
                                    aria-haspopup="true"
                                    aria-expanded={open ? "true" : undefined}
                                    onClick={handleMenuOpen}
                                  >
                                    <MoreHorizIcon />
                                  </IconButton>
                                  <Menu
                                    anchorEl={anchorEl}
                                    id="basic-menu"
                                    open={open}
                                    onClose={handleMenuClose}
                                  >
                                    <MenuItem
                                      sx={{ fontSize: "14px" }}
                                      onClick={(e) =>
                                        handleEditTitle(e, caseItem._id)
                                      }
                                    >
                                      <EditIcon sx={{ fontSize: 16, mr: 1 }} />
                                      Edit
                                    </MenuItem>
                                    <MenuItem
                                      sx={{
                                        fontSize: "14px",
                                        color: "#d32f2f",
                                      }}
                                      onClick={(e) => handleDeleteChat(e)}
                                    >
                                      <DeleteIcon
                                        color="error"
                                        sx={{ fontSize: 16, mr: 1 }}
                                      />
                                      Delete
                                    </MenuItem>
                                  </Menu>
                                </>
                              )}
                            </Stack>
                          )}
                        </ListItemButton>
                      </ListItem>
                      {/* Divider */}
                      <Divider
                        sx={{
                          mx: 1.5,
                          backgroundColor: "#E0E0E0",
                        }}
                      />
                    </Box>
                  ))
              )}
            </AccordionDetails>
          </Accordion>
        ))
      ) : (
        !isCollapsed && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              flexDirection: "column",
              m: "auto",
            }}
          >
            <Typography
              variant="subtitle2"
              color="textSecondary"
              textAlign="center"
            >
              You have no previous case added.
              <br />
              Add a new case! 🚀
            </Typography>
          </Box>
        )
      )}
      <ConfirmationDialog
        open={openDeleteDialog}
        onClose={() => {
          setOpenDeleteDialog(false);
        }}
        onConfirm={(e) => {
          if (openDeleteDialog) {
            handleConfirmDelete(e, selectedCase?._id);
          }
        }}
        title="Confirm Delete"
        contentText={`Are you sure you want to delete the case "${selectedCase?.caseTitle}"? This action cannot be undone.`}
        confirmButtonText="Delete"
        cancelButtonText="Cancel"
        fetchCaseLoading={fetchCaseLoading}
      />
      <AddNewCaseModal
        open={addNewCaseModal}
        onClose={() => {
          setAddNewCaseModal(false);
        }}
      />
    </Box>
  );
}
